import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { SpaceResponse } from 'src/features/spaces/api/types/response/space.response';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { TableColumn } from 'src/types/interfaces/table-column';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { SpacesTableRow } from '../molecules/spaces-table-row';

interface Props {
  data: SpaceResponse[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  onRemoveFromGroup: (spaceToken: string) => void;
}

export const SpacesTable = ({
  isLoading,
  data,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  onRemoveFromGroup,
}: Props) => {
  const { t } = useTranslation(['spaceGroups']);

  const columns: TableColumn[] = [
    {
      id: 'name',
      label: t('spaceGroups:details.columns.name'),
      isSortable: true,
    },
    {
      id: 'floor',
      label: t('spaceGroups:details.columns.floor'),
      isSortable: true,
    },
    {
      id: 'type',
      label: t('spaceGroups:details.columns.type'),
      isSortable: false,
    },
    {
      id: 'state',
      label: t('spaceGroups:details.columns.state'),
      isSortable: false,
    },
    {
      id: 'remove',
      label: t('spaceGroups:details.columns.remove'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(
              ({
                token,
                name,
                type,
                floor,
                spaceUnavailabilityStatePeriods,
              }) => {
                return (
                  <SpacesTableRow
                    key={token}
                    token={token}
                    name={name}
                    type={type}
                    floor={floor}
                    spaceUnavailabilityStates={
                      spaceUnavailabilityStatePeriods ?? []
                    }
                    onRemoveFromGroup={onRemoveFromGroup}
                  />
                );
              },
            )}
          </TableBody>
        </Table>
      </Scrollbar>
      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
